import React, { useCallback, useState } from "react";
import { PageLayout } from "src/components/shopify/PageLayout";
import { useTranslation } from "react-i18next";
import { Box, DataTable, IndexTable, Layout, TextField } from "@shopify/polaris";
import { PolarisCard } from "src/components/shopify/PolarisCard";
import {
  LoadProductWithSKU,
  ProductSelectedProp,
} from "src/components/shopify/products/quick_product/LoadProductWithSKU";
import { PolarisEmptyState } from "src/components/shopify/PolarisEmptyState";
import { Image } from "src/components/shopify/Image";
import { PolarisText } from "src/components/shopify/PolarisrText";
import { ProductShipmentInput, useMerchantProductShipmentMutation } from "src/graphql/codegen_graphql";
import { Modal } from "src/hooks/modal";
import { Toast } from "src/hooks/toast";

export function InventoryShipmentScreen() {
  const { t } = useTranslation();
  const [selectProduct, setSelectProduct] = useState<ProductSelectedProp[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [createShipment, { loading }] = useMerchantProductShipmentMutation({
    refetchQueries: ["supplierShipmentList"],
  });

  const handleQtyInput = useCallback(
    (value, sku_index: number, index: number) => {
      let qty = value;
      if (value === "") {
        qty = "1";
      }
      const items = [...products];
      items[index].sku[sku_index].qty = qty;
      setProducts(items);
    },
    [products],
  );

  const handleCreateShipment = useCallback(() => {
    const input: ProductShipmentInput[] = products.map((x) => {
      return x.sku.map((sku: any) => {
        return {
          item_id: Number(x.product.id),
          sku_id: Number(sku.id),
          color: sku.color,
          size: sku.size,
          qty: Number(sku.qty),
        };
      });
    });

    Modal.dialog({
      title: t("confirmation"),
      body: (
        <div>
          {t("create_shipment_message")} {input.flat().length} SKU.
        </div>
      ),
      buttons: [
        { title: t("cancel"), class: "danger" },
        {
          title: t("confirm"),
          class: "primary",
          onPress: () => {
            createShipment({
              variables: {
                data: input.flat(),
              },
            }).then((res) => {
              if (res.data?.merchantProductShipment) {
                Toast.onShow("Shipment was created", "success", "right");
              }
            });
          },
        },
      ],
    });
  }, [t, products, createShipment]);

  const grand_total =
    products.length > 0
      ? products
          .map((x) => x.sku)
          .flat()
          .reduce((a, b) => (a = a + Number(b.qty) * Number(b.price)), 0)
      : 0;

  return (
    <PageLayout
      pageProp={{
        title: t("create_shipment"),
        primaryAction: { content: t("create"), onAction: handleCreateShipment, loading, disabled: loading },
      }}
    >
      <Layout>
        <Layout.Section>
          <PolarisCard roundedAbove="sm" padding={"0"}>
            <Box padding={"400"} borderColor="border" borderBlockEndWidth="025">
              <LoadProductWithSKU
                initialProductSelected={selectProduct}
                onLoadProductInfo={setProducts}
                onProductSelectedChange={setSelectProduct}
              />
            </Box>
            <Box>
              <IndexTable
                headings={[
                  { title: t("product") },
                  { title: t("color") },
                  { title: t("size") },
                  { title: t("qty") },
                  { title: t("price") },
                ]}
                itemCount={products.length}
                selectable={false}
                emptyState={<PolarisEmptyState />}
              >
                {products.map((x, i) => {
                  const title = Object.values(x.product.title).filter((f) => !!f && f !== "MultiLangString")[0];
                  return (
                    <React.Fragment key={i}>
                      <IndexTable.Row tone="subdued" selected={false} id={i + ""} position={i}>
                        <IndexTable.Cell>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Image alt="" source={x.product.picture} size="small" />
                            <div style={{ width: 150 }} className="ml-3">
                              <PolarisText as="p" variant="bodySm" truncate>
                                {title}
                              </PolarisText>
                              <small>L192: {x.product.code}</small>
                            </div>
                          </div>
                        </IndexTable.Cell>
                        {x.sku.length === 1 ? (
                          <React.Fragment>
                            <IndexTable.Cell>{x.sku[0].color}</IndexTable.Cell>
                            <IndexTable.Cell>{x.sku[0].size}</IndexTable.Cell>
                            <IndexTable.Cell>
                              <div style={{ width: 150, position: "relative" }}>
                                <TextField
                                  autoComplete="off"
                                  label
                                  labelHidden
                                  value={x.sku[0].qty}
                                  onChange={(v) => handleQtyInput(v, 0, i)}
                                  type="number"
                                />
                              </div>
                            </IndexTable.Cell>
                            <IndexTable.Cell>${x.sku[0].price}</IndexTable.Cell>
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            <IndexTable.Cell></IndexTable.Cell>
                            <IndexTable.Cell></IndexTable.Cell>
                            <IndexTable.Cell></IndexTable.Cell>
                            <IndexTable.Cell></IndexTable.Cell>
                          </React.Fragment>
                        )}
                      </IndexTable.Row>
                      {x.sku.length > 1 &&
                        x.sku.map((sku: any, j: number) => {
                          return (
                            <IndexTable.Row id={j + ""} position={j} key={j}>
                              <IndexTable.Cell></IndexTable.Cell>
                              <IndexTable.Cell>{sku.color}</IndexTable.Cell>
                              <IndexTable.Cell>{sku.size}</IndexTable.Cell>
                              <IndexTable.Cell>
                                <div style={{ width: 150, position: "relative" }}>
                                  <TextField
                                    autoComplete="off"
                                    label
                                    labelHidden
                                    value={sku.qty}
                                    onChange={(v) => handleQtyInput(v, j, i)}
                                    type="number"
                                  />
                                </div>
                              </IndexTable.Cell>
                              <IndexTable.Cell>${sku.price}</IndexTable.Cell>
                            </IndexTable.Row>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </IndexTable>
            </Box>
          </PolarisCard>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <PolarisCard roundedAbove="sm" padding={"0"}>
            <Box padding={"300"} borderColor="border" borderBlockEndWidth="025">
              <PolarisText as="h2" variant="headingSm">
                {t("summary")}
              </PolarisText>
            </Box>
            <Box>
              <DataTable
                headings={[t("description"), <div className="text-end">{t("amount")}</div>]}
                columnContentTypes={["text", "text"]}
                rows={[
                  [t("grand_total"), <div className="text-end">${grand_total.toFixed(2)}</div>],
                  [t("shipment_total"), <div className="text-end">${grand_total.toFixed(2)}</div>],
                ]}
              />
            </Box>
          </PolarisCard>
          {/* <PolarisCard roundedAbove="sm" padding={"0"}>
            <Box padding={"4"} borderColor="border" borderBlockEndWidth="025">
              <PolarisText as="h2" variant="headingSm">
                {t("recent_shipment")}
              </PolarisText>
            </Box>
            <Box>
              <InventoryRecentShipment polaris />
            </Box>
          </PolarisCard> */}
        </Layout.Section>
      </Layout>
    </PageLayout>
  );
}

/*
export class InventoryShipmentScreen extends Component {
  state: {
    shipItems: any[];
  };

  constructor(props: any) {
    super(props);
    this.state = {
      shipItems: [],
    };
  }

  // componentDidMount() {
  //   document.getElementById("DesktopMenu")!.className =
  //     "min-full-height menu-w color-scheme-dark color-style-bright menu-position-side menu-side-left menu-layout-mini sub-menu-style-over sub-menu-color-bright selected-menu-color-light menu-activated-on-hover menu-has-selected-link";
  // }

  // componentWillUnmount() {
  //   document.getElementById("DesktopMenu")!.className =
  //     "min-full-height menu-w color-scheme-light color-style-transparent menu-position-side menu-side-left menu-layout-compact sub-menu-style-over sub-menu-color-bright selected-menu-color-light menu-activated-on-hover menu-has-selected-link";
  // }

  onClickProduct(item: any) {
    const index = this.state.shipItems.findIndex((x) => x.id === item.id);

    if (index < 0) {
      this.setState({
        shipItems: [
          ...this.state.shipItems,
          {
            ...item,
            sku: [item.sku],
          },
        ],
      });
    } else {
      const indexSku = this.state.shipItems[index].sku.findIndex(
        (x: any) => x.size === item.sku.size && x.color === item.sku.color,
      );
      const shipItems = this.state.shipItems;
      if (indexSku < 0) {
        shipItems[index].sku.push(item.sku);
        this.setState({
          shipItems,
        });
      } else {
        shipItems[index].sku[indexSku].qty++;
        this.setState({
          shipItems,
        });
      }
    }
  }

  render() {
    let total = 0;
    for (const item of this.state.shipItems) {
      for (const sku of item.sku) {
        total = total + Number(sku.qty) * Number(item.price);
      }
    }

    return (
      <Content breadcrumb={<BreadCrumb items={BreadCrumbItems} />}>
        <div className="row">
          <div className="col-md-4">
            <div className="ecommerce-customer-info">
              <InventoryProductList onClick={(e) => this.onClickProduct(e)} {...this.props} />
            </div>
          </div>
          <div className="col-md-8">
            <div className="order-box">
              <div className="order-details-box">
                <div className="order-main-info">
                  <strong>Shipment</strong>
                </div>
              </div>
              <div className="order-controls">
                <InventoryAddShipment
                  items={this.state.shipItems}
                  onAddShipment={() => this.setState({ shipItems: [] })}
                />
              </div>
              <div className="order-items-table">
                <div className="table-responsive">
                  <table className="table table-lightborder">
                    <thead>
                      <tr>
                        <th>PRODUCT INFO</th>
                        <th></th>
                        <th>COLOR</th>
                        <th>SIZE</th>
                        <th style={{ width: 170 }} className="text-center">
                          QUANTITY
                        </th>
                        <th className="text-right">PRICE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.state.shipItems.map((x, i) => {
                        return x.sku.map(({ color, size, qty }: any, j: number) => {
                          return (
                            <tr key={i}>
                              {j === 0 && (
                                <td rowSpan={x.sku.length}>
                                  <div
                                    className="product-image"
                                    style={{
                                      backgroundImage: `url(${x.picture})`,
                                      marginRight: 20,
                                    }}
                                  ></div>
                                </td>
                              )}
                              {j === 0 && (
                                <td rowSpan={x.sku.length}>
                                  <div className="product-name">Supplier Code: {x.supplierCode}</div>
                                  <div className="product-details">L192: {x.code}</div>
                                </td>
                              )}
                              <td className="product-details" style={{ textTransform: "uppercase" }}>
                                {color}
                              </td>
                              <td className="product-details" style={{ textTransform: "uppercase" }}>
                                {size}
                              </td>
                              <td className="text-center">
                                <div className="quantity-selector">
                                  <div className="quantity-input">
                                    <div className="input-group">
                                      <div className="input-group-prepend input-group-sm">
                                        <div
                                          className="input-group-text"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => {
                                            const shipItems = this.state.shipItems;
                                            shipItems[i].sku[j].qty--;

                                            if (shipItems[i].sku[j].qty === 0) {
                                              shipItems[i].sku.splice(j, 1);
                                            }

                                            this.setState({ shipItems });
                                          }}
                                        >
                                          <div className="os-icon os-icon-minus2"></div>
                                        </div>
                                      </div>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={qty}
                                        onChange={(e) => {
                                          const shipItems = this.state.shipItems;
                                          shipItems[i].sku[j].qty =
                                            Number(e.target.value) < 0 || !e.target.value ? 0 : e.target.value;
                                          this.setState({ shipItems });
                                        }}
                                        onBlur={() => {
                                          const shipItems = this.state.shipItems;
                                          if (shipItems[i].sku[j].qty === 0) {
                                            shipItems[i].sku.splice(j, 1);
                                          }
                                          this.setState({ shipItems });
                                        }}
                                        style={{ textAlign: "center" }}
                                      />
                                      <div className="input-group-append input-group-sm">
                                        <div
                                          className="input-group-text"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => {
                                            const shipItems = this.state.shipItems;
                                            shipItems[i].sku[j].qty++;
                                            this.setState({ shipItems });
                                          }}
                                        >
                                          <div className="os-icon os-icon-plus"></div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              {j === 0 && (
                                <td rowSpan={x.sku.length} className="product-price text-right">
                                  ${x.price}
                                </td>
                              )}
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="order-foot">
                <div className="row">
                  <div className="col-md-6 mb-4"></div>
                  <div className="col-md-5 offset-md-1">
                    <div className="order-summary-row as-total">
                      <div className="order-summary-label">Shipment Total</div>
                      <div className="order-summary-value">${total.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <br />
            <br />
            <InventoryRecentShipment />
          </div>
        </div>
      </Content>
    );
  }
}
*/
